import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Container,
  ContentLayout,
  Header,
  FormField,
  Button,
  SpaceBetween,
  Flashbar,
  Box,
  Alert,
  FileUpload,
  StatusIndicator,
  Select
} from '@amzn/awsui-components-react';
import { 
  useUploadTestSuiteMutation,
  useGetTestSuitesQuery,
  useGetTestSuiteDetailQuery,
  useLazyGetTestSuiteVersionQuery
} from 'src/store/apis/testSuiteApi';
import { TestSuiteInfo } from 'src/store/interfaces/TestSuite.types';
import useNotification from '../../hooks/useNotification';
import { usePermissions } from '../../hooks/usePermissions';

interface ValidationError {
  message: string;
  line?: number;
  column?: number;
}

interface SelectedOption {
  label: string;
  value: string;
}

const CreateTestSuite: React.FC = () => {
  const { addNotificationItem, notificationItems } = useNotification();
  const { canManageTestSuites } = usePermissions();
  
  // API hooks
  const { data: testSuites, isLoading: isLoadingTestSuites } = useGetTestSuitesQuery();
  const [uploadTestSuite, { isLoading: isUploading }] = useUploadTestSuiteMutation();
  const [getTestSuiteVersion, { isLoading: isLoadingVersion }] = useLazyGetTestSuiteVersionQuery();
  
  // Download state
  const [selectedTestSuiteForDownload, setSelectedTestSuiteForDownload] = useState<SelectedOption | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<SelectedOption | null>(null);
  const [downloadPreviewContent, setDownloadPreviewContent] = useState<string>('');
  
  // Step 2: Auto-fetch test suite details when selectedTestSuiteForDownload changes
  const { data: testSuiteDetail, isLoading: isLoadingDetail } = useGetTestSuiteDetailQuery(
    selectedTestSuiteForDownload?.value || '', 
    { skip: !selectedTestSuiteForDownload }
  );
  
  // Upload state
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [jsonContent, setJsonContent] = useState<string>('');
  const [parsedJson, setParsedJson] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isValidJson, setIsValidJson] = useState<boolean>(false);

  // Validate JSON content
  const validateJson = useCallback((content: string): boolean => {
    if (!content.trim()) {
      setValidationErrors([{ message: 'File is empty' }]);
      setIsValidJson(false);
      setParsedJson(null);
      return false;
    }

    try {
      const parsed = JSON.parse(content);
      
      // Validate that the parsed JSON has an 'id' field
      if (!parsed.id || typeof parsed.id !== 'string' || parsed.id.trim() === '') {
        setValidationErrors([{ message: 'Test suite must have a valid "id" field' }]);
        setIsValidJson(false);
        setParsedJson(null);
        return false;
      }
      
      setParsedJson(parsed);
      setValidationErrors([]);
      setIsValidJson(true);
      return true;
    } catch (error) {
      const jsonError = error as SyntaxError;
      const errorMessage = jsonError.message;
      
      // Try to extract line/column information from error message
      const lineMatch = errorMessage.match(/line (\d+)/);
      const columnMatch = errorMessage.match(/column (\d+)/);
      
      setValidationErrors([{
        message: errorMessage,
        line: lineMatch ? parseInt(lineMatch[1], 10) : undefined,
        column: columnMatch ? parseInt(columnMatch[1], 10) : undefined
      }]);
      setIsValidJson(false);
      setParsedJson(null);
      return false;
    }
  }, []);

  // Handle file upload
  const handleFileChange = useCallback(({ detail }: { detail: { value: File[] } }) => {
    const files = detail.value;
    setUploadedFiles(files);
    
    if (files.length === 0) {
      setJsonContent('');
      setParsedJson(null);
      setValidationErrors([]);
      setIsValidJson(false);
      return;
    }

    const file = files[0];
    
    // Check file type
    if (!file.name.toLowerCase().endsWith('.json') && file.type !== 'application/json') {
      setValidationErrors([{ message: 'Please upload a JSON file (.json)' }]);
      setIsValidJson(false);
      setJsonContent('');
      setParsedJson(null);
      return;
    }

    // Read file content
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setJsonContent(content);
      validateJson(content);
    };
    reader.onerror = () => {
      setValidationErrors([{ message: 'Failed to read file' }]);
      setIsValidJson(false);
      setJsonContent('');
      setParsedJson(null);
    };
    reader.readAsText(file);
  }, [validateJson]);

  // Handle JSON content change in editor
  const handleJsonContentChange = useCallback((content: string) => {
    setJsonContent(content);
    validateJson(content);
  }, [validateJson]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!isValidJson || !parsedJson) {
      addNotificationItem({
        type: 'error',
        content: 'Please upload and validate a JSON file before submitting',
        id: 'validation-error',
      });
      return;
    }

    try {
      await uploadTestSuite(parsedJson).unwrap();
      
      addNotificationItem({
        type: 'success',
        content: 'Test Suite uploaded successfully',
        id: 'test-suite-uploaded',
      });

      // Reset form using the extracted function
      resetUploadForm();

    } catch (error) {
      const errorMessage = (error as any)?.data?.message || (error as any)?.message || 'Unknown error occurred';
      
      addNotificationItem({
        type: 'error',
        content: `Failed to upload test suite: ${errorMessage}`,
        id: 'test-suite-upload-error'
      });
    }
  };

  const testSuiteOptionsForDownload = useMemo(() => {
    if (!testSuites) return [];
    return testSuites.map((suite: TestSuiteInfo) => ({
      label: suite.id,
      value: suite.id
    }));
  }, [testSuites]);

  const versionOptions = useMemo(() => {
    if (!testSuiteDetail?.versions) return [];
    
    return testSuiteDetail.versions.map((version) => ({
      label: version.isLatest 
        ? 'Latest Version' 
        : new Date(version.lastModified).toLocaleString(),
      value: version.versionId
    }));
  }, [testSuiteDetail]);

  const handleVersionChange = useCallback(async (selectedOption: SelectedOption) => {
    setSelectedVersion(selectedOption);
    
    if (!selectedTestSuiteForDownload) return;

    try {
      const result = await getTestSuiteVersion({
        testSuiteId: selectedTestSuiteForDownload.value,
        versionId: selectedOption.value
      }).unwrap();
      
      const contentToDisplay = result.content || result;
      const content = JSON.stringify(contentToDisplay, null, 2);
      setDownloadPreviewContent(content);
    } catch (error) {

      addNotificationItem({
        type: 'error',
        content: 'Failed to load test suite version',
        id: 'version-load-error'
      });
      setDownloadPreviewContent('');
    }
  }, [selectedTestSuiteForDownload, getTestSuiteVersion, addNotificationItem]);

  const handleDownloadTestSuiteChange = useCallback((selectedOption: SelectedOption | null) => {
    setSelectedTestSuiteForDownload(selectedOption);
    setSelectedVersion(null);
    setDownloadPreviewContent('');
  }, []);

  // Reset upload form
  const resetUploadForm = useCallback(() => {
    setUploadedFiles([]);
    setJsonContent('');
    setParsedJson(null);
    setValidationErrors([]);
    setIsValidJson(false);
  }, []);

  // Handle download
  const handleDownload = useCallback(() => {
    if (!downloadPreviewContent || !selectedTestSuiteForDownload) return;

    const blob = new Blob([downloadPreviewContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    const versionLabel = selectedVersion?.label === 'Latest Version' ? 'latest' : selectedVersion?.label?.replace(/[^\w\-]/g, '_') || 'version';
    const filename = `${selectedTestSuiteForDownload.value}_${versionLabel}.json`;
    
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    addNotificationItem({
      type: 'success',
      content: `Test suite downloaded as ${filename}`,
      id: 'download-success'
    });
  }, [downloadPreviewContent, selectedTestSuiteForDownload, selectedVersion, addNotificationItem]);

  // Step 3 & 4: When testSuiteDetail loads -> auto-select latest and load content
  useEffect(() => {
    if (testSuiteDetail?.versions && selectedTestSuiteForDownload && !isLoadingDetail) {
      const latestVersion = testSuiteDetail.versions.find(v => v.isLatest);
      if (latestVersion) {
        const latestOption = {
          label: 'Latest Version',
          value: latestVersion.versionId
        };
        setSelectedVersion(latestOption);
        handleVersionChange(latestOption);
      }
    }
  }, [testSuiteDetail, isLoadingDetail]); // Only depend on testSuiteDetail, not selectedTestSuiteForDownload

  const getValidationStatusIndicator = () => {
    if (uploadedFiles.length === 0) {
      return <StatusIndicator type="pending">No file uploaded</StatusIndicator>;
    }
    if (validationErrors.length > 0) {
      return <StatusIndicator type="error">Invalid JSON</StatusIndicator>;
    }
    if (isValidJson) {
      return <StatusIndicator type="success">Valid JSON</StatusIndicator>;
    }
    return <StatusIndicator type="loading">Validating...</StatusIndicator>;
  };

  // Determine if this is an update or create operation by checking if the id exists in the system
  const submitButtonText = useMemo(() => {
    if (!parsedJson?.id) {
      return 'Create New Test Suite';
    }
    
    // Check if the uploaded file's id matches any existing test suite
    const existingTestSuite = testSuites?.find((suite: TestSuiteInfo) => suite.id === parsedJson.id);
    return existingTestSuite ? 'Update Existing Test Suite' : 'Create New Test Suite';
  }, [parsedJson, testSuites]);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Upload a JSON file containing test suite configuration"
        >
          Create Test Suite
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Flashbar items={notificationItems} stackItems />
        
        {!canManageTestSuites && (
          <Alert type="info" statusIconAriaLabel="Info">
            You have view-only access to this page. Sufficient permission is required to make changes.
          </Alert>
        )}
        
        {/* Download Section */}
        <Container
          header={
            <Header
              variant="h2"
              description="Download existing test suite files in JSON format"
            >
              Download Test Suite
            </Header>
          }
        >
          <SpaceBetween size="l">
            <FormField
              label="Select Test Suite"
              description="Choose a test suite to download"
              stretch
            >
              <Select
                selectedOption={selectedTestSuiteForDownload}
                onChange={({ detail }) => handleDownloadTestSuiteChange(detail.selectedOption as SelectedOption)}
                options={testSuiteOptionsForDownload}
                placeholder="Choose a test suite"
                disabled={isLoadingTestSuites}
                loadingText="Loading test suites..."
                empty="No test suites available"
              />
            </FormField>

            {/* Version Selection */}
            {selectedTestSuiteForDownload && (
              <FormField
                label="Select Version"
                description="Choose a version to preview and download"
                stretch
              >
                <Select
                  selectedOption={selectedVersion}
                  onChange={({ detail }) => handleVersionChange(detail.selectedOption as SelectedOption)}
                  options={versionOptions}
                  placeholder="Choose a version"
                  disabled={isLoadingDetail}
                  loadingText="Loading versions..."
                  empty="No versions available"
                />
              </FormField>
            )}

            {/* Download Preview */}
            {downloadPreviewContent && (
              <FormField
                label="Test Suite Preview"
                description="Preview of the selected test suite version"
                stretch
              >
                <Box>
                  <textarea
                    value={downloadPreviewContent}
                    readOnly
                    className="json-preview-textarea"
                    placeholder="Test suite content will appear here..."
                    style={{ minHeight: '300px' }}
                  />
                </Box>
              </FormField>
            )}

            {/* Download Actions */}
            {selectedTestSuiteForDownload && selectedVersion && (
              <Box textAlign="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="link"
                    onClick={() => {
                      setSelectedTestSuiteForDownload(null);
                      setSelectedVersion(null);
                      setDownloadPreviewContent('');
                    }}
                  >
                    Clear Selection
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleDownload}
                    disabled={!downloadPreviewContent || isLoadingVersion}
                    loading={isLoadingVersion}
                    loadingText="Loading version..."
                  >
                    Download JSON
                  </Button>
                </SpaceBetween>
              </Box>
            )}
          </SpaceBetween>
        </Container>

        {/* Upload Section */}
        {canManageTestSuites && (
        <Container
          header={
            <Header
              variant="h2"
              description="Upload and validate your test suite JSON file"
            >
              File Upload
            </Header>
          }
        >
          <SpaceBetween size="l">
            <FormField 
              label="Test Suite JSON File"
              description="Select a JSON file containing your test suite configuration"
              stretch
            >
              <FileUpload
                onChange={handleFileChange}
                value={uploadedFiles}
                i18nStrings={{
                  uploadButtonText: e => e ? "Choose files" : "Choose file",
                  dropzoneText: e => e ? "Drop files to upload" : "Drop file to upload",
                  removeFileAriaLabel: e => `Remove file ${e + 1}`,
                  limitShowFewer: "Show fewer files",
                  limitShowMore: "Show more files",
                  errorIconAriaLabel: "Error"
                }}
                multiple={false}
                accept=".json,application/json"
                showFileLastModified
                showFileSize
                constraintText="Only JSON files are accepted"
              />
            </FormField>

            {/* Validation Status */}
            <Box>
              <strong>Validation Status:</strong> {getValidationStatusIndicator()}
            </Box>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <Alert
                statusIconAriaLabel="Error"
                type="error"
                header="JSON Validation Errors"
              >
                <SpaceBetween size="xs">
                  {validationErrors.map((error, index) => (
                    <Box key={`validation-error-${index}-${error.message.substring(0, 20)}`}>
                      {error.line && error.column 
                        ? `Line ${error.line}, Column ${error.column}: ${error.message}`
                        : error.message
                      }
                    </Box>
                  ))}
                </SpaceBetween>
              </Alert>
            )}

            {/* JSON Content Preview */}
            {jsonContent && (
              <FormField 
                label="JSON Content Preview"
                description="Review and edit your JSON content below"
                stretch
              >
                <Box>
                  <textarea
                    value={jsonContent}
                    onChange={(e) => handleJsonContentChange(e.target.value)}
                    className="json-preview-textarea"
                    placeholder="JSON content will appear here..."
                  />
                </Box>
              </FormField>
            )}

            {/* Submit Button */}
            <Box textAlign="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  disabled={uploadedFiles.length === 0}
                  onClick={resetUploadForm}
                >
                  Clear
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={!isValidJson || uploadedFiles.length === 0}
                  loading={isUploading}
                  loadingText="Uploading test suite..."
                >
                  {submitButtonText}
                </Button>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        </Container>
        )}

      </SpaceBetween>
    </ContentLayout>
  );
};

export default CreateTestSuite;
